import {
  Document,
  HeadingLevel,
  Packer,
  Paragraph,
  Table,
  TableCell,
  TableLayoutType,
  TableRow,
  TextRun,
  WidthType,
} from "docx";
import { lightTheme } from "styles/theme";

import { exportDataToCsv } from "lib/exportArrayToCsv";
import { BidNoBidResponse } from "lib/hooks/api/bid/answers/useGetBidReportAnswers";
import { BidReport } from "lib/hooks/api/bid/bid_reports/useBidReport";
import { BidDetails } from "lib/hooks/api/bid/bids/useBid";
import { downloadFile, sanitizeFilename } from "lib/utils";
import {
  allNoBorders,
  buildDocxFooter,
  buildMetaRow,
  buildMetaTable,
  buildQaTable,
  buildTextLines,
  DOCX_COL1_WIDTH,
  DOCX_COL2_WIDTH,
  DOCX_PAGE_WIDTH,
  noBorders,
} from "../common_report_components/docxExportUtils";

type CsvRow = { question: string; answer: string };

const placeholderColor = lightTheme.colors.sysTextPlaceholder.replace("#", "");

export function exportQualificationReportAsCsv(
  answersData: BidNoBidResponse,
  winThemes?: BidDetails["winThemes"],
  bidName?: string,
): void {
  const rows: CsvRow[] = [
    { question: "Report link", answer: window.location.href },
    { question: "Question", answer: "Answer" },
  ];

  const summaryGroup = answersData.find((g) => g.groupName === "Summary");
  const summaryText = summaryGroup?.questions[0]?.answer?.answer;
  if (summaryText) {
    rows.push({ question: "Summary", answer: summaryText });
  }

  const qualificationQuestions = answersData
    .filter((g) => g.groupName !== "Summary")
    .flatMap((g) => g.questions);
  if (qualificationQuestions.length > 0) {
    rows.push({ question: "Qualification questions", answer: "" });
    for (const q of qualificationQuestions) {
      rows.push({ question: q.title, answer: q.answer?.answer || "" });
    }
  }

  if (winThemes && winThemes.length > 0) {
    rows.push({ question: "Win themes", answer: "" });
    for (const theme of winThemes) {
      rows.push({ question: theme.title, answer: theme.description });
    }
  }

  const filename = `Qualification_report_${sanitizeFilename(bidName || "")}.csv`;
  exportDataToCsv(filename, rows, false);
}

export async function exportQualificationReportAsDocx(
  answersData: BidNoBidResponse,
  winThemes: BidDetails["winThemes"] | undefined,
  bidName: string | undefined,
  notice?: BidReport["notice"],
): Promise<void> {
  const children: (Paragraph | Table)[] = [
    new Paragraph({
      heading: HeadingLevel.HEADING_1,
      children: [new TextRun("Qualification report")],
      spacing: { before: 200, after: 200 },
    }),
  ];

  // Metadata
  const metaRows: TableRow[] = [];
  if (notice?.name) {
    metaRows.push(
      buildMetaRow("Notice title", notice.name, `${window.location.origin}/notices/${notice.guid}`),
    );
  }
  const buyer = notice?.buyers?.[0];
  if (buyer?.name) {
    metaRows.push(
      buildMetaRow("Buyer", buyer.name, `${window.location.origin}/buyers/${buyer.guid}`),
    );
  }
  if (metaRows.length > 0) {
    children.push(buildMetaTable(metaRows));
  }

  children.push(
    new Paragraph({
      children: [
        new TextRun({
          text: "Review the information below to qualify this opportunity.",
          italics: true,
          color: placeholderColor,
        }),
      ],
      spacing: { before: 200, after: 200 },
    }),
  );

  // Summary section
  const summaryGroup = answersData.find((g) => g.groupName === "Summary");
  const summaryText = summaryGroup?.questions[0]?.answer?.answer;
  if (summaryText) {
    children.push(
      new Paragraph({
        heading: HeadingLevel.HEADING_2,
        children: [new TextRun("Summary")],
        spacing: { before: 200, after: 200 },
      }),
    );
    children.push(
      new Paragraph({ children: buildTextLines(summaryText), spacing: { after: 200 } }),
    );
  }

  // Q&A section
  for (const group of answersData.filter((g) => g.groupName !== "Summary")) {
    children.push(
      new Paragraph({
        heading: HeadingLevel.HEADING_2,
        children: [new TextRun(group.groupName)],
        spacing: { before: 200, after: 200 },
      }),
    );
    children.push(buildQaTable(group));
  }

  // Win themes section
  if (winThemes && winThemes.length > 0) {
    children.push(
      new Paragraph({
        heading: HeadingLevel.HEADING_2,
        children: [new TextRun("Win themes")],
        spacing: { before: 200, after: 200 },
      }),
    );
    children.push(
      new Table({
        width: { size: DOCX_PAGE_WIDTH, type: WidthType.DXA },
        layout: TableLayoutType.FIXED,
        columnWidths: [DOCX_COL1_WIDTH, DOCX_COL2_WIDTH],
        borders: allNoBorders,
        rows: winThemes.map(
          (theme) =>
            new TableRow({
              children: [
                new TableCell({
                  width: { size: DOCX_COL1_WIDTH, type: WidthType.DXA },
                  borders: noBorders,
                  children: [
                    new Paragraph({
                      children: [new TextRun({ text: theme.title, bold: true })],
                    }),
                  ],
                }),
                new TableCell({
                  width: { size: DOCX_COL2_WIDTH, type: WidthType.DXA },
                  borders: noBorders,
                  children: [new Paragraph({ children: buildTextLines(theme.description) })],
                }),
              ],
            }),
        ),
      }),
    );
  }

  const doc = new Document({
    sections: [
      {
        properties: {
          page: { margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 } },
        },
        footers: { default: buildDocxFooter() },
        children,
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  downloadFile(blob, `Qualification_report_${sanitizeFilename(bidName || "")}.docx`);
}
